<script setup lang="ts">
/**
 * ApprovalCard — renders a single pending resident approval request.
 *
 * Key rules (spec §2.4 + plan Task 10):
 * - Checkbox rendered ONLY when !requiresDualApproval (absent, not just disabled, for flagged)
 * - FlagCallout shown when requiresDualApproval
 * - DualApprovalBanner shown when signedOffBy.length > 0 && requiresDualApproval
 * - Approve button hidden when current user is the first approver (banner handles this)
 */
import BaseBadge from '@/components/ui/BaseBadge.vue';
import FlagCallout from './FlagCallout.vue';
import DualApprovalBanner from './DualApprovalBanner.vue';
import type { ResidentApprovalRequestDto } from '@/api/boardTypes';
import { useAuth } from '@/stores/auth';

interface Props {
  request: ResidentApprovalRequestDto;
  isSelected?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  select: [id: string];
  approve: [id: string];
  deny: [id: string];
}>();

const auth = useAuth();

/** True when the current user already gave the first sign-off on a dual-approval request */
const currentUserAlreadyApproved = () =>
  props.request.requiresDualApproval &&
  props.request.signedOffBy.some((s) => s.approverUserId === auth.user?.userId);

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function verificationBadgeVariant(status: string) {
  if (status === 'Verified') return 'approved';
  if (status === 'Failed') return 'denied';
  return 'pending';
}

function verificationLabel(method: string, status: string): string {
  const methodLabel: Record<string, string> = {
    MailedCode: 'Mailed code',
    DocumentUpload: 'Document upload',
    NeighborVouch: 'Neighbor vouch',
  };
  return `${methodLabel[method] ?? method} · ${status}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
</script>

<template>
  <article
    :data-testid="`approval-card-${request.id}`"
    :class="[
      'bg-surface border rounded-lg overflow-hidden mb-3.5 transition-shadow hover:shadow-sm',
      request.requiresDualApproval ? 'border-orange-100' : 'border-border',
    ]"
  >
    <!-- Card head -->
    <div
      :class="[
        'px-5 py-4 border-b border-border flex gap-3.5 items-center',
        request.requiresDualApproval ? 'bg-orange-50' : '',
      ]"
    >
      <!-- Checkbox — ONLY for non-flagged requests -->
      <input
        v-if="!request.requiresDualApproval"
        type="checkbox"
        :checked="isSelected"
        class="w-4 h-4 accent-navy-700 flex-shrink-0"
        :aria-label="`Select ${request.residentName}`"
        @change="emit('select', request.id)"
      >

      <!-- Avatar -->
      <div
        class="w-11 h-11 rounded-pill bg-teal-600 text-white grid place-items-center font-bold text-body flex-shrink-0"
      >
        {{ initials(request.residentName) }}
      </div>

      <!-- Name + meta -->
      <div class="flex-1 min-w-0">
        <div class="text-body font-semibold text-fg">{{ request.residentName }}</div>
        <div class="text-caption text-muted font-mono tabular-nums mt-0.5">
          {{ request.residentEmail }}
          <template v-if="request.residentPhone"> · {{ request.residentPhone }}</template>
        </div>
      </div>

      <!-- Status + verification badges -->
      <div class="flex items-center gap-2 flex-wrap justify-end flex-shrink-0">
        <BaseBadge variant="pending">Pending</BaseBadge>
        <BaseBadge :variant="verificationBadgeVariant(request.verificationStatus)" :no-dot="true">
          {{ verificationLabel(request.verificationMethod, request.verificationStatus) }}
        </BaseBadge>
      </div>
    </div>

    <!-- Flag callout (only for dual-approval) -->
    <FlagCallout
      v-if="request.requiresDualApproval"
      :risk-signals-json="request.riskSignalsJson"
      class="mt-3.5"
    />

    <!-- Dual approval banner (when first sign-off recorded) -->
    <DualApprovalBanner
      v-if="request.requiresDualApproval && request.signedOffBy.length > 0"
      :signed-off-by="request.signedOffBy"
    />

    <!-- Details grid -->
    <div class="grid grid-cols-1 sm:grid-cols-3 border-t border-border">
      <div class="px-5 py-3.5 sm:border-r border-border last:border-r-0 border-b sm:border-b-0">
        <div class="text-caption font-bold uppercase tracking-wider text-muted-2 mb-1.5">Claimed property</div>
        <div class="text-body font-semibold text-fg">{{ request.claimedProperty ?? '—' }}</div>
      </div>
      <div class="px-5 py-3.5 sm:border-r border-border last:border-r-0 border-b sm:border-b-0">
        <div class="text-caption font-bold uppercase tracking-wider text-muted-2 mb-1.5">Verification</div>
        <div class="text-body font-semibold text-fg">{{ request.verificationMethod.replace(/([A-Z])/g, ' $1').trim() }}</div>
        <div v-if="request.verificationNote" class="text-caption text-muted mt-0.5">{{ request.verificationNote }}</div>
      </div>
      <div class="px-5 py-3.5">
        <div class="text-caption font-bold uppercase tracking-wider text-muted-2 mb-1.5">Submitted</div>
        <div class="text-body font-semibold text-fg font-mono tabular-nums">{{ formatDate(request.submittedAt) }}</div>
      </div>
    </div>

    <!-- Footer actions -->
    <div class="px-5 py-3.5 bg-canvas-soft border-t border-border flex items-center gap-3 flex-wrap">
      <div class="flex-1 min-w-0">
        <p class="text-caption text-muted truncate">
          No previous account
          <template v-if="request.requiresDualApproval">
            · <strong class="text-orange-600">2-board-member review required</strong>
          </template>
        </p>
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <button
          class="h-8 px-2.5 text-caption text-muted hover:text-fg border border-border rounded-md hover:bg-canvas hover:border-border-strong transition-colors"
          @click="emit('deny', request.id)"
        >
          Deny
        </button>

        <!-- Approve button: hidden when current user already gave first sign-off -->
        <button
          v-if="!currentUserAlreadyApproved()"
          class="h-8 px-3 text-caption text-white bg-navy-800 hover:bg-navy-900 rounded-md transition-colors font-medium flex items-center gap-1.5"
          @click="emit('approve', request.id)"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" class="w-3.5 h-3.5">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          Approve
        </button>
      </div>
    </div>
  </article>
</template>
